import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LandingNavbar from '../components/LandingNavbar';

/**
 * /auto-login?token=<welcomeToken>
 *
 * Redeems a welcome magic token issued when an admin creates a staff user
 * (or resets their password). Fetches a real session from the backend,
 * hands it to AuthContext.finalizeLogin, then navigates straight to the
 * force-change-password flow. Reloading this URL is safe — the server
 * issues a fresh session each time.
 */
export default function AutoLogin() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { finalizeLogin } = useAuth();
  const [error, setError] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    // Strict-mode guard: effect runs twice in dev. A second exchange of the
    // same token is safe (server doesn't invalidate it on read), but we'd
    // waste a round-trip + double-render flashes.
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setError('الرابط غير صالح — الرمز مفقود');
      return;
    }

    (async () => {
      try {
        const axios = (await import('axios')).default;
        const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
        const res = await axios.post(`${apiBase}/auth/welcome-login`, { token });
        const payload = res.data?.data || res.data;
        const sessionToken = payload.token;
        const user = payload.user;
        const tenant = payload.tenant;
        const subscription = payload.subscription;

        // Pipe through AuthContext so permissions + localStorage follow the
        // same normal-login bootstrap.
        await finalizeLogin({
          token: sessionToken,
          user,
          tenant,
          subscription,
        });

        // Pass must_change_password to the redirect target via state. The
        // Login page's force-change UI key off AuthContext state will pick
        // this up thanks to the shared `must_change_password` on the user.
        if (payload.must_change_password) {
          navigate('/force-change-password', { replace: true, state: { tempCreds: { token: sessionToken, user, tenant, subscription } } });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'تعذّر الدخول، حاول مرة أخرى';
        setError(msg);
      }
    })();
  }, [token, navigate, finalizeLogin]);

  return (
    <>
      <LandingNavbar />
      <div
        className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16"
        dir="rtl"
        style={{ backgroundColor: '#FDFCF9' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-[420px] bg-white rounded-3xl border border-[#E8E5DD] shadow-sm p-8 text-center"
        >
          <div className="flex justify-center mb-5">
            <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
          </div>

          {error ? (
            <>
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: '#FEF2F2' }}
              >
                <AlertCircle className="w-6 h-6" style={{ color: '#B91C1C' }} />
              </div>
              <h2 className="text-lg font-bold mb-2" style={{ color: '#1A1A1A' }}>
                الرابط غير صالح
              </h2>
              <p className="text-sm leading-[1.7]" style={{ color: '#666' }}>
                {error}
              </p>
              <button
                onClick={() => navigate('/staff-login', { replace: true })}
                className="mt-5 inline-flex items-center justify-center h-11 px-5 rounded-xl text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)' }}
              >
                تسجيل الدخول يدوياً
              </button>
            </>
          ) : (
            <>
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: '#E1F5EE' }}
              >
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0F6E56' }} />
              </div>
              <h2 className="text-lg font-bold mb-1" style={{ color: '#1A1A1A' }}>
                جارٍ تسجيل الدخول...
              </h2>
              <p className="text-sm" style={{ color: '#888' }}>
                لحظة واحدة من فضلك
              </p>
            </>
          )}
        </motion.div>
      </div>
    </>
  );
}
